import type { CSSProperties, ReactNode } from 'react';
import { Backdrop } from '../../../modules/design-system';
import { MyPacksFooter } from '../../MyPacks/MyPacksFooter';
import { DeliveryHeader } from './DeliveryHeader';

export interface DeliveryPageShellProps {
  title: string;
  onBack: () => void;
  backLabel?: string;
  /** Drop the noise-SVG overlay from the page gradient. Mirrors the
   *  same flag on MyPacksPage so the whole flow stays consistent. */
  hideBackgroundPattern?: boolean;
  children: ReactNode;
}

/**
 * Common chrome for Delivery New / Show pages — backdrop, optional
 * hero photo layer (driven by `--co-MyPacksPageBackground-bgImage`),
 * sticky header and the My Packs footer. The body is whatever the
 * page renders inside, vertically centred in a max-w-640 column.
 *
 * `data-page="my-packs"` (not "delivery") pulls in the same
 * `--ui-bg-gradient` overrides from index.css that paint the rest of
 * the My Packs flow — neutral dark grey + noise — so this screen
 * inherits the page palette by default.
 */
export function DeliveryPageShell({
  title,
  onBack,
  backLabel,
  hideBackgroundPattern = false,
  children,
}: DeliveryPageShellProps) {
  return (
    <div
      data-page="my-packs"
      style={
        hideBackgroundPattern
          ? ({ '--co-MyPacksPageBackground-noiseOpacity': 0 } as CSSProperties)
          : undefined
      }
    >
      <Backdrop bgImageUrl={null} />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
        style={{
          fontFamily:
            'var(--co-MyPacksHeader-fontFamily), Inter, system-ui, sans-serif',
        }}
      >
        <DeliveryHeader
          title={title}
          onBack={onBack}
          {...(backLabel !== undefined && { backLabel })}
        />
        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
          <main className="flex-1 px-4 pb-12 @[600px]:px-6">
            <div className="mx-auto w-full max-w-[640px] py-4">{children}</div>
          </main>
          <MyPacksFooter />
        </div>
      </div>
    </div>
  );
}
